import React, { useEffect, useRef, useState } from 'react';

const SearchIcon = () => (
  <svg
    data-bbox="33 33 133.358 133.358"
    viewBox="0 0 200 200"
    height="20"
    width="20"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    aria-hidden="true"
  >
    <g>
      <path
        d="M54.758 119.575c-8.658-8.658-13.425-20.167-13.425-32.408 0-12.234 4.767-23.742 13.425-32.4 8.659-8.659 20.167-13.434 32.409-13.434 12.241 0 23.75 4.775 32.408 13.434C128.233 63.425 133 74.933 133 87.167c0 12.241-4.767 23.75-13.425 32.408C110.917 128.233 99.408 133 87.167 133c-12.242 0-23.75-4.767-32.409-13.425Zm111.6 40.892-38.083-38.092c8.425-9.808 13.058-22.133 13.058-35.208 0-14.467-5.633-28.067-15.866-38.292C115.242 38.642 101.633 33 87.167 33 72.7 33 59.1 38.642 48.867 48.875 38.633 59.1 33 72.7 33 87.167c0 14.475 5.633 28.066 15.867 38.3C59.1 135.7 72.7 141.333 87.167 141.333c13.075 0 25.4-4.633 35.216-13.066l38.084 38.091 5.891-5.891Z"
        fillRule="evenodd"
      />
    </g>
  </svg>
);

const ChevronIcon = ({ isOpen }) => (
  <svg
    className={`h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06Z"
      clipRule="evenodd"
    />
  </svg>
);

const navItems = [
  { label: 'Home', href: 'https://www.mandarintutorhk.com/' },
  {
    label: 'About Us',
    href: 'https://www.mandarintutorhk.com/about',
    children: [
      { label: 'Join Our Team', href: 'https://www.mandarintutorhk.com/join-our-team' },
    ],
  },
  {
    label: 'Learn Mandarin',
    children: [
      { label: 'Private Mandarin Lessons', href: 'https://www.mandarintutorhk.com/private-mandarin-lessons' },
      { label: 'Mandarin Classes', href: 'https://www.mandarintutorhk.com/mandarin-classes' },
      { label: 'HSK Prep Course', href: 'https://www.mandarintutorhk.com/hsk-preparation-course-hong-kong' },
      { label: 'Business Mandarin', href: 'https://www.mandarintutorhk.com/business-mandarin-lessons' },
    ],
  },
  {
    label: 'Learn Cantonese',
    children: [
      { label: 'Cantonese Tutoring', href: 'https://www.mandarintutorhk.com/cantonese-tutor-hong-kong' },
      { label: 'Cantonese Classes', href: 'https://www.mandarintutorhk.com/cantonese-classes' },
      {
        label: 'Cantonese Lessons for English Speakers',
        href: 'https://www.mandarintutorhk.com/cantonese-lesson-hong-kong-english',
      },
    ],
  },
  {
    label: 'Teens & Kids',
    children: [
      { label: 'IB Chinese', href: 'https://www.mandarintutorhk.com/ib-chinese-tutor-hk' },
      { label: 'IGCSE Chinese', href: 'https://www.mandarintutorhk.com/igcse-chinese-tutor-hk' },
      {
        label: 'Chinese Homework Helper',
        href: 'https://www.mandarintutorhk.com/chinese-homework-helper-hk',
      },
    ],
  },
  {
    label: 'Pricing & Booking',
    children: [
      { label: 'Booking', href: 'https://book.mandarintutorhk.com/' },
      { label: 'Pricing', href: 'https://www.mandarintutorhk.com/chinese-tutor-hong-kong-pricing' },
    ],
  },
];

const focusClasses =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black';

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDesktopMenu, setOpenDesktopMenu] = useState(null);
  const [openMobileMenus, setOpenMobileMenus] = useState({});
  const navigationRef = useRef(null);

  const closeAllMenus = () => {
    setIsMobileMenuOpen(false);
    setOpenDesktopMenu(null);
    setOpenMobileMenus({});
  };

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (navigationRef.current && !navigationRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
        setOpenDesktopMenu(null);
        setOpenMobileMenus({});
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setOpenDesktopMenu(null);
        setOpenMobileMenus({});
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const toggleMobileSubmenu = (label) => {
    setOpenMobileMenus((current) => ({
      ...current,
      [label]: !current[label],
    }));
  };

  return (
    <nav ref={navigationRef} className="bg-black text-white border-b border-gray-800" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop layout: logo bottom-aligned with nav links, right side stacked */}
        <div className="hidden lg:flex items-end justify-between py-4 gap-6">
          {/* Logo - bottom aligned with nav links */}
          <div className="flex-shrink-0">
            <a
              href="https://www.mandarintutorhk.com/"
              className={`text-2xl font-bold text-white hover:text-gray-300 transition-colors no-underline rounded-sm ${focusClasses}`}
            >
              Aileen<span className="text-purple-500">.</span>
            </a>
            <p className="text-xs text-gray-400 mt-0.5">Mandarin &amp; Cantonese Tutor HK</p>
          </div>

          {/* Right side: search + CTA on top, nav links on bottom (right-aligned) */}
          <div className="flex flex-col items-end gap-2 min-w-0">
            <div className="flex items-center gap-4">
              <a
                href="https://www.mandarintutorhk.com/?search="
                aria-label="Search"
                className={`text-white hover:text-purple-400 transition-colors rounded-sm ${focusClasses}`}
              >
                <SearchIcon />
              </a>
              <a
                href="https://book.mandarintutorhk.com/"
                className={`bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 transition-colors no-underline whitespace-nowrap rounded-sm ${focusClasses}`}
              >
                Book Your Chinese Class Now
              </a>
            </div>
            <div className="flex items-center gap-3 xl:gap-5">
              {navItems.map((item) => {
                if (!item.children) {
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      className={`text-sm font-medium text-white hover:text-purple-400 transition-colors no-underline whitespace-nowrap rounded-sm ${focusClasses}`}
                    >
                      {item.label}
                    </a>
                  );
                }

                const isOpen = openDesktopMenu === item.label;
                const submenuId = `desktop-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}-menu`;

                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setOpenDesktopMenu(item.label)}
                    onMouseLeave={() => setOpenDesktopMenu(null)}
                    onFocus={() => setOpenDesktopMenu(item.label)}
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget)) {
                        setOpenDesktopMenu(null);
                      }
                    }}
                  >
                    <div className="flex items-center">
                      {item.href ? (
                        <>
                          <a
                            href={item.href}
                            className={`text-sm font-medium text-white hover:text-purple-400 transition-colors no-underline whitespace-nowrap rounded-sm ${focusClasses}`}
                          >
                            {item.label}
                          </a>
                          <button
                            type="button"
                            className={`ml-1 p-0.5 text-white hover:text-purple-400 transition-colors rounded-sm ${focusClasses}`}
                            aria-label={`${isOpen ? 'Close' : 'Open'} ${item.label} menu`}
                            aria-haspopup="true"
                            aria-expanded={isOpen}
                            aria-controls={submenuId}
                            onClick={() => setOpenDesktopMenu(isOpen ? null : item.label)}
                          >
                            <ChevronIcon isOpen={isOpen} />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className={`flex items-center gap-1 text-sm font-medium text-white hover:text-purple-400 transition-colors whitespace-nowrap rounded-sm ${focusClasses}`}
                          aria-haspopup="true"
                          aria-expanded={isOpen}
                          aria-controls={submenuId}
                          onClick={() => setOpenDesktopMenu(isOpen ? null : item.label)}
                        >
                          {item.label}
                          <ChevronIcon isOpen={isOpen} />
                        </button>
                      )}
                    </div>

                    {isOpen && (
                      <div
                        id={submenuId}
                        className="absolute left-0 top-full z-50 min-w-64 rounded-md border border-gray-700 bg-black py-2 shadow-xl"
                      >
                        {item.children.map((child) => (
                          <a
                            key={child.label}
                            href={child.href}
                            onClick={() => setOpenDesktopMenu(null)}
                            className={`block px-4 py-2.5 text-sm text-white hover:bg-gray-900 hover:text-purple-400 transition-colors no-underline whitespace-nowrap ${focusClasses}`}
                          >
                            {child.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile and tablet layout: logo + hamburger */}
        <div className="lg:hidden flex items-center justify-between py-4">
          <div className="flex-shrink-0">
            <a
              href="https://www.mandarintutorhk.com/"
              onClick={closeAllMenus}
              className={`text-2xl font-bold text-white hover:text-gray-300 transition-colors no-underline rounded-sm ${focusClasses}`}
            >
              Aileen<span className="text-purple-500">.</span>
            </a>
            <p className="text-xs text-gray-400 mt-0.5">Mandarin &amp; Cantonese Tutor HK</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setIsMobileMenuOpen((isOpen) => !isOpen);
              setOpenMobileMenus({});
            }}
            className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-purple-400 hover:bg-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-400"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation-menu"
            aria-label={`${isMobileMenuOpen ? 'Close' : 'Open'} main menu`}
          >
            {!isMobileMenuOpen ? (
              <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            ) : (
              <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile and tablet menu */}
      {isMobileMenuOpen && (
        <div id="mobile-navigation-menu" className="lg:hidden border-t border-gray-800">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => {
              if (!item.children) {
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={closeAllMenus}
                    className={`block px-3 py-2 rounded-md text-base font-medium text-white hover:text-purple-400 hover:bg-gray-900 transition-colors no-underline ${focusClasses}`}
                  >
                    {item.label}
                  </a>
                );
              }

              const isOpen = Boolean(openMobileMenus[item.label]);
              const submenuId = `mobile-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}-menu`;

              return (
                <div key={item.label}>
                  <div className="flex items-center rounded-md hover:bg-gray-900">
                    {item.href ? (
                      <>
                        <a
                          href={item.href}
                          onClick={closeAllMenus}
                          className={`flex-1 px-3 py-2 text-base font-medium text-white hover:text-purple-400 transition-colors no-underline rounded-md ${focusClasses}`}
                        >
                          {item.label}
                        </a>
                        <button
                          type="button"
                          onClick={() => toggleMobileSubmenu(item.label)}
                          className={`mr-2 p-2 text-white hover:text-purple-400 transition-colors rounded-md ${focusClasses}`}
                          aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${item.label} menu`}
                          aria-haspopup="true"
                          aria-expanded={isOpen}
                          aria-controls={submenuId}
                        >
                          <ChevronIcon isOpen={isOpen} />
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleMobileSubmenu(item.label)}
                        className={`flex flex-1 items-center justify-between px-3 py-2 text-left text-base font-medium text-white hover:text-purple-400 transition-colors rounded-md ${focusClasses}`}
                        aria-haspopup="true"
                        aria-expanded={isOpen}
                        aria-controls={submenuId}
                      >
                        {item.label}
                        <ChevronIcon isOpen={isOpen} />
                      </button>
                    )}
                  </div>

                  {isOpen && (
                    <div id={submenuId} className="ml-4 border-l border-gray-700 pl-2">
                      {item.children.map((child) => (
                        <a
                          key={child.label}
                          href={child.href}
                          onClick={closeAllMenus}
                          className={`block px-3 py-2 rounded-md text-sm font-medium text-gray-200 hover:text-purple-400 hover:bg-gray-900 transition-colors no-underline ${focusClasses}`}
                        >
                          {child.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <a
              href="https://book.mandarintutorhk.com/"
              onClick={closeAllMenus}
              className={`block mt-2 mx-3 text-center bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded transition-colors no-underline ${focusClasses}`}
            >
              Book Your Chinese Class Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
